import {
  DecoderBackupTracker,
  isDebugWebrtcLoggingEnabled,
  type ClientStreamStatsMessage,
  type DecoderBackupClassification,
  type DecoderBackupObservation,
  type DecoderBackupTransition,
  type StreamHealthEvent,
} from '@playcastdotio/PeerCore';
import { sinkWebrtcDebugLine } from './webrtcDebugSink';

export type DecoderBackupMonitorDeps = {
  /** Health-event sink — the StreamHealthManager bus. Production, not debug-gated. */
  emitHealth: (peerId: string, event: StreamHealthEvent) => void;
  nowMs?: () => number;
};

/**
 * DecoderBackupMonitor — makes a guest decoder backing up VISIBLE, on every channel we have.
 *
 * The Android field report: ~250 ms of felt latency on a clean local Wi-Fi at 360p/30fps,
 * while every network meter read healthy. The frames were sitting in the phone's decoder.
 * Nothing in the debug log or the stream-health record named that state; it had to be
 * inferred from a playout trend and an fps number. This class names it:
 *
 * - `[DecoderBackup]` NDJSON line on EVERY tick the decoder is backed up (debug builds), with
 *   the depth in frames and ms and where it came from (`depthSource`: frame counters on
 *   Chromium/Android, decoder residency on Safari, which has no frame counters), the decode
 *   rate against the rate actually DELIVERED, the decoder's duty cycle, and the
 *   classification — `throughput-deficit` (decoder cannot sustain the send rate) or
 *   `standing-depth` (decoder keeps pace but is sitting on a queue; cutting fps makes this
 *   WORSE). One more line when it clears, with duration and peak.
 *
 * `decodeDutyCyclePct` is the field to read first when a session feels laggy on a clean
 * link: a backed-up decoder idling most of the time is queueing, not struggling, and no
 * amount of bitrate or framerate reduction will drain it.
 * - `decoder-backup` stream-health events (production): one when a backup begins (held past
 *   the dwell), one when it ends. Two events per backup, not one per tick.
 *
 * Observability only — it never acts. The response to a backup lives in the recovery
 * manager and the tier selection; keeping the eyes apart from the hands means this record
 * stays trustworthy while the response is being changed. The one thing it hands the hands
 * is the classification (`classificationFor`): a `standing-depth` backup must never be
 * answered with a tier change, and the recovery manager checks that here before it acts.
 */
export class DecoderBackupMonitor {
  private readonly tracker: DecoderBackupTracker;
  private readonly nowMs: () => number;
  /** The latest tick's classification per peer, for the recovery manager's tier decision. */
  private readonly classificationByPeer = new Map<string, DecoderBackupClassification>();

  constructor(private readonly deps: DecoderBackupMonitorDeps) {
    this.nowMs = deps.nowMs ?? (() => Date.now());
    this.tracker = new DecoderBackupTracker({ nowMs: this.nowMs });
  }

  /** Observe one guest clientStats tick against the fps the host actually put on the wire. */
  public observe(peerId: string, msg: ClientStreamStatsMessage, deliveredFps: number): void {
    const episodeActive = msg.decodeLag?.active === true;
    // The flat fields win; an older guest carries the same readings inside its episode.
    const result = this.tracker.observe(peerId, {
      standingBacklogFrames: msg.standingBacklogFrames ?? msg.decodeLag?.standingBacklogFrames,
      decodeMsPerFrame: msg.decodeMsPerFrame ?? msg.decodeLag?.decodeMsPerFrame,
      decodeFps: msg.decodeFps ?? msg.decodeLag?.decodeFps,
      deliveredFps,
      playoutLatencyTrendMs: msg.playoutLatencyTrendMs,
      episodeActive,
    });
    this.classificationByPeer.set(peerId, result.observation.classification);

    this.logDebugLine(peerId, result.observation, result.transition, result.ended);

    if (result.transition === 'began') {
      this.deps.emitHealth(peerId, {
        kind: 'decoder-backup',
        stream: 'video',
        atMs: this.nowMs(),
        detail: {
          phase: 'began',
          classification: result.observation.classification,
          depthSource: result.observation.depthSource,
          standingBacklogFrames: result.observation.standingBacklogFrames ?? -1,
          standingBacklogMs: result.observation.standingBacklogMs ?? -1,
          decodeMsPerFrame: result.observation.decodeMsPerFrame ?? -1,
          decodeFps: result.observation.decodeFps ?? -1,
          deliveredFps: result.observation.deliveredFps,
          decodeDeficitPct: result.observation.decodeDeficitPct,
          decodeDutyCyclePct: result.observation.decodeDutyCyclePct ?? -1,
        },
      });
    } else if (result.transition === 'ended' && result.ended) {
      this.deps.emitHealth(peerId, {
        kind: 'decoder-backup',
        stream: 'video',
        atMs: this.nowMs(),
        detail: {
          phase: 'ended',
          durationMs: Math.round(result.ended.durationMs),
          peakFrames: result.ended.peakFrames,
          peakMs: result.ended.peakMs,
        },
      });
    }
  }

  /**
   * How the latest tick classified this peer's decoder: `throughput-deficit` (cannot sustain
   * the delivered rate — fewer or smaller frames help), `standing-depth` (keeping pace behind
   * a queue — a tier change reconfigures the decoder and lengthens it), `none`, or undefined
   * before the first tick.
   */
  public classificationFor(peerId: string): DecoderBackupClassification | undefined {
    return this.classificationByPeer.get(peerId);
  }

  public clearPeer(peerId: string): void {
    this.tracker.clearKey(peerId);
    this.classificationByPeer.delete(peerId);
  }

  public clearAll(): void {
    this.tracker.clearAll();
    this.classificationByPeer.clear();
  }

  /**
   * DEBUG BUILD ONLY — one `[DecoderBackup]` line per backed-up tick, plus the clearing edge.
   * Silent while the decoder is keeping up so a clean session's log is not padded.
   */
  private logDebugLine(
    peerId: string,
    observation: DecoderBackupObservation,
    transition: DecoderBackupTransition,
    ended: { durationMs: number; peakFrames: number; peakMs: number } | undefined,
  ): void {
    if (!isDebugWebrtcLoggingEnabled()) return;
    if (transition === 'clear') return;
    const payload = {
      peerId,
      transition,
      classification: observation.classification,
      depthSource: observation.depthSource,
      standingBacklogFrames: observation.standingBacklogFrames ?? 'absent',
      standingBacklogMs: observation.standingBacklogMs ?? 'absent',
      decodeMsPerFrame: observation.decodeMsPerFrame ?? 'absent',
      decodeFps: observation.decodeFps ?? 'absent',
      deliveredFps: observation.deliveredFps,
      decodeDeficitPct: observation.decodeDeficitPct,
      decodeDutyCyclePct: observation.decodeDutyCyclePct ?? 'absent',
      playoutLatencyTrendMs: observation.playoutLatencyTrendMs ?? 'absent',
      episodeActive: observation.episodeActive,
      ...(ended
        ? { durationMs: Math.round(ended.durationMs), peakFrames: ended.peakFrames, peakMs: ended.peakMs }
        : {}),
    };
    console.log('[DecoderBackup]', payload);
    sinkWebrtcDebugLine(JSON.stringify({ t: new Date().toISOString(), tag: '[DecoderBackup]', ...payload }));
  }
}
